import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'

/**
 * Tix ticketing iframe bridge. The iframe/postMessage handling and the `watch` on `lastMessage` are
 * client-only.
 */
/** The Tix session payload the iframe posts back, and what `update` stores from it. */
export type TixSessionData = { order?: any; profile?: any; user?: any }

export const useTixStore = defineStore('tix', () => {
  const config = useRuntimeConfig()
  const { locale } = useI18n()

  const order = ref<any>(undefined)
  const orderSize = ref<number | undefined>(undefined)
  const profile = ref<any>(undefined)
  const user = ref<any>(undefined)
  const tixEnv = ref<'tix' | 'education'>('tix')
  const isEducationCustomer = ref(false)
  const customerId = ref<any>(null)
  const iframe = ref<HTMLIFrameElement | undefined>(undefined)
  // Not a ref: it's a client-only closure (set right before triggering the iframe postMessage
  // round-trip it responds to) with no meaningful initial-SSR value, and a function can't be part of
  // Pinia's serialized SSR state (the hydration payload can't stringify it).
  let callback: () => void = () => undefined
  const lastMessage = ref<MessageEvent | null>(null)

  function setTixEnv(newTixEnv: string) {
    switch (newTixEnv) {
      case 'tix':
      case 'education':
        tixEnv.value = newTixEnv
        break
      default:
        console.error('invalid tix env var given')
    }
  }
  function update({ order: newOrder, profile: newProfile, user: newUser }: TixSessionData) {
    const userTagIds = newUser?.tags.map((tag: any) => tag.id) ?? []
    // Note that keys will always be converted to String
    const educationTagIds = Object.keys(educationSchoolTypes)

    order.value = newOrder
    orderSize.value = newOrder?.length || 0
    profile.value = newProfile
    user.value = newUser
    isEducationCustomer.value = tixEnv.value === 'education' && userTagIds.some((tagId: any) => educationTagIds.includes(String(tagId)))
  }
  function setCustomerId(newCustomerId: any) {
    customerId.value = newCustomerId
  }
  // Accepts null because that is what a template ref hands over before mount and after unmount; the
  // store itself keeps using `undefined` for "no iframe", which every guard here already tests for.
  function setIframe(node: HTMLIFrameElement | null | undefined) {
    iframe.value = node ?? undefined
  }
  function setCallback(newCallback: () => void) {
    callback = newCallback
  }
  // Stable wrapper exposed as `callback` below: since `callback` itself is a reassignable `let`, not a
  // ref, the store's return object would otherwise capture its initial value instead of staying in
  // sync with later setCallback() calls.
  function invokeCallback() {
    return callback()
  }
  function setLastMessage(message: MessageEvent | null) {
    lastMessage.value = message
  }

  function call({ call, reload = true }: { call: any; reload?: boolean }) {
    setLastMessage(null)
    if (!iframe.value) {
      return Promise.reject(new Error('Tix - iframe not set'))
    }
    const currentTixUrl = tixEnv.value === 'education' ? config.public.edutixSiteUrl : config.public.tixSiteUrl
    if (reload !== false) {
      setCallback(() => {
        iframe.value?.contentWindow?.postMessage(call, currentTixUrl)
      })
      /** This will trigger the "onload" event of the iframe in {@see TixState.vue}
       * Which will call the callback from above */
      iframe.value.src = `${currentTixUrl}/${locale.value}/itix`
    } else {
      iframe.value.contentWindow?.postMessage(call, currentTixUrl)
    }
    return new Promise<TixSessionData>((resolve, reject) => {
      if (lastMessage.value) {
        resolve(lastMessage.value?.data)
        return
      }
      const timeoutId = setTimeout(() => {
        unwatchHandle()
        reject(new Error('Tix - iframe call timeout'))
      }, 10000)
      const unwatchHandle = watch(
        () => lastMessage.value,
        (messageEvent) => {
          unwatchHandle()
          clearTimeout(timeoutId)
          resolve(messageEvent?.data)
        },
      )
    })
  }
  function response({ messageEvent }: { messageEvent: MessageEvent }) {
    const expectedTixOrigin = tixEnv.value === 'education' ? config.public.edutixSiteUrl : config.public.tixSiteUrl
    if (messageEvent.origin !== expectedTixOrigin) {
      return // Ignore non-tix messages
    }
    setLastMessage(messageEvent)
  }

  return {
    order,
    orderSize,
    profile,
    user,
    tixEnv,
    isEducationCustomer,
    customerId,
    iframe,
    callback: invokeCallback,
    lastMessage,
    setTixEnv,
    update,
    setCustomerId,
    setIframe,
    setCallback,
    setLastMessage,
    call,
    response,
  }
})
